import { useEffect, useState } from "react";
import axios from "axios";
import { LessonsSideBar } from "../../components/LessonsSideBar";
import { TitleBar } from "../../components/TitleBar";
import { CourseProgress } from "../../components/CourseProgress";
import baseURL from "../../config/api";
import { FaCheckCircle, FaEye, FaQuestionCircle, FaTimesCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { VideoPlayer } from "./VideoPlayer";
import { VideoSideBar } from "../../components/VideoSideBar";
import Cookies from "js-cookie";
import { Quiz } from "./Quiz";
import { QuizExerciseSideBar } from "../../components/QuizExerciseSideBar";
import { ShowCompleteContent } from "../../components/ShowCompleteContent";
import { LessonProgress } from "../../components/LessonProgress";
import { UserProgress } from "../../components/UserProgress";
import { ConfirmPopup } from "../../components/ConfirmPopup";
import { SubLessonsProgress } from "../../components/SubLessonsProgress";
import useConfirm from "../../hook/useConfirm";

export const LessonPage = () => {
    const location = useLocation();
    const role = Cookies.get("role-elministry") || "";
    const { courseId, lessonId, subLessonId, isLearner, isLogin } = location.state || {};
    const userId = Cookies.get("userId-elministry") || "";
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [course, setCourse] = useState<any>(null);
    const [showLesson, setShowLesson] = useState(true);
    const [lessons, setLessons] = useState<any[]>([]);
    const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);

    const [subLesson, setSubLesson] = useState<any>({});
    const [selectedVideo, setSelectedVideo] = useState<number | null>(null);
    const [selectedContent, setSelectedContent] = useState<number | null>(null);
    const [selectedSubLesson, setSelectedSubLesson] = useState<number | null>(null);
    const [selectedQuiz, setSelectedQuiz] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const [quizExercises, setQuizExercises] = useState<any[]>([]);
    const [selectedSubLessonForQuiz, setSelectedSubLessonForQuiz] = useState<any | null>(null);

    const [attempts, setAttempts] = useState<any[]>([]);
    const [selectedSubmission, setSelectedSubmission] = useState<any | null>(null);
    const [attemptQuestions, setAttemptQuestions] = useState<any[]>([]);
    const [showAttemptsPopup, setShowAttemptsPopup] = useState(false);
    const [attemptLoading, setAttemptLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(5);
    const [totalCount, setTotalCount] = useState(0);
    let dashboardNavigation = "";

    switch (role) {
        case "Admin":
            dashboardNavigation = "/admin/users";
            break;

        case "Contributor":
            dashboardNavigation = "/contributor/dashboard";
            break;

        case "Teacher":
            dashboardNavigation = "/teacher/dashboard";
            break;

        case "Parent":
            dashboardNavigation = "/parent/dashboard";
            break;

        case "Learner":
            dashboardNavigation = "/learner/dashboard";
            break;
    }

    useEffect(() => {
        if (showAttemptsPopup) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => document.body.classList.remove("overflow-hidden");
    }, [showAttemptsPopup]);

    const fetchAttempts = async (quizId: number, pageNum = page, pageSize = size) => {
        if (!userId || (!quizId && quizId !== 0)) return;

        setAttemptLoading(true);
        setSelectedSubmission(null);
        setAttemptQuestions([]);

        try {
            const res = await axios.get(
                `${baseURL}submissions/GetSubmissionsPagingByUserIdAndByQuizId/${userId},${quizId},${pageNum},${pageSize}`
            );

            if (res.data && Array.isArray(res.data.submissions)) {
                setAttempts(res.data.submissions);
                setTotalCount(res.data.count || 0);
            } else {
                setAttempts([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error("Error fetching submissions:", err);
            setAttempts([]);
            setTotalCount(0);
        } finally {
            setAttemptLoading(false);
        }
    };

    useEffect(() => {
        if (showAttemptsPopup && selectedQuiz?.id) {
            fetchAttempts(selectedQuiz.id, page, size);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, showAttemptsPopup, selectedQuiz?.id]);


    const viewAttemptDetails = async (submission: any) => {
        if (!userId || !submission?.id) return;

        const submissionId = submission.id;
        setSelectedSubmission(submission);
        setAttemptQuestions([]);
        setAttemptLoading(true);

        try {
            const res = await axios.get(
                `${baseURL}submissionAnswers/GetSubmissionAnswersByUserIdAndBySubmissionId/${userId},${submissionId}`
            );

            const groupedQuestions = Object.values(
                res.data.reduce((acc: any, item: any) => {
                    const {
                        questionId,
                        selectedAnswerId,
                        isSelectedAnswerCorrect,
                    } = item;

                    // First occurrence of this question
                    if (!acc[questionId]) {
                        acc[questionId] = {
                            ...item,
                            selectedAnswerIds: [selectedAnswerId], // store ALL selections
                            isSelectedAnswerCorrect, // initial state
                        };
                    }
                    // Question already exists
                    else {
                        acc[questionId].selectedAnswerIds.push(selectedAnswerId);

                        // If ANY selected answer is wrong → question is incorrect
                        if (!isSelectedAnswerCorrect) {
                            acc[questionId].isSelectedAnswerCorrect = false;
                        }
                    }

                    return acc;
                }, {})
            );

            setAttemptQuestions(groupedQuestions);


            setAttemptQuestions(groupedQuestions);

        } catch (err) {
            console.error("Error fetching submission answers:", err);
            setAttemptQuestions([]);
        } finally {
            setAttemptLoading(false);
        }
    };

    useEffect(() => {
        const fetchCourse = async () => {
            if (!courseId) return;
            try {
                const res = await axios.get(`${baseURL}courses/${courseId}`);
                setCourse(res.data);
            } catch (error) {
                console.error("Error fetching course:", error);
            }
        };
        fetchCourse();
    }, [courseId]);

    useEffect(() => {
        const fetchLessons = async () => {
            if (!courseId) return;

            try {
                setLessons([]);
                setSubLesson(null);
                const lessonRes = await axios.get(
                    `${baseURL}lessons/GetLessonsByCourseId/${courseId}`
                );
                const lessons = lessonRes.data || [];


                const lessonsWithSubs = await Promise.all(
                    lessons.map(async (lesson: any) => {
                        try {
                            const subRes = await axios.get(
                                `${baseURL}sublessons/GetSubLessonsByLessonId/${lesson.id}`
                            );


                            const subLessons = subRes.data?.map((sub: any) => ({
                                sublessonId: sub.id,
                                subLessonTitle: sub.title,
                            })).sort(
                                (a: any, b: any) => a.orderNumber - b.orderNumber
                            ) || [];


                            return { ...lesson, subLessons };
                        } catch (err) {
                            console.error(`Error fetching sublessons for lesson ${lesson.id}:`, err);
                            return { ...lesson, subLessons: [] };
                        }
                    })
                );


                setLessons(lessonsWithSubs);


                if (!lessonId)
                    setSelectedLessonId(lessonsWithSubs[0]?.id);
                else
                    setSelectedLessonId(lessonId);

                if ((!isLogin || !isLearner) && !subLessonId)
                    setSelectedSubLesson(lessonsWithSubs[0]?.subLessons[0]?.sublessonId);
                else if (subLessonId)
                    setSelectedSubLesson(subLessonId);
                else
                    setSelectedSubLesson(null);

            } catch (error) {
                console.error("Error fetching lessons:", error);
            }
        };

        fetchLessons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId]);

    useEffect(() => {
        const fetchSubLessonData = async () => {
            if (!selectedSubLesson) return;

            setLoading(true);
            setSubLesson(null);

            try {
                // 1️⃣ Fetch sublesson details
                const { data: subLesson } = await axios.get(
                    `${baseURL}sublessons/${selectedSubLesson}`
                );

                if (!subLesson?.id) {
                    setSubLesson(null);
                    return;
                }

                // 2️⃣ Fetch related data in parallel
                const [orderRes, exerciseRes, quizRes] = await Promise.allSettled([
                    axios.get(`${baseURL}subLessonOrders/GetSubLessonOrderReportBySubLessonId/${subLesson.id}`),
                    axios.get(`${baseURL}exercises/GetExercisesBySubLessonId/${subLesson.id}`),
                    axios.get(`${baseURL}quizzes/GetQuizzesNotRelatedExercisesBySubLessonId/${subLesson.id}`),
                ]);

                // 3️⃣ Extract data safely (ignore failed requests)
                const sublessonOrderByData =
                    orderRes.status === "fulfilled"
                        ? [...orderRes.value.data].sort((a, b) => a.orderNumber - b.orderNumber)
                        : [];

                const exercises =
                    exerciseRes.status === "fulfilled" ? exerciseRes.value.data || [] : [];

                const quizzes =
                    quizRes.status === "fulfilled" ? quizRes.value.data || [] : [];

                // 4️⃣ Combine and set to state
                setSubLesson({
                    ...subLesson,
                    sublessonOrderByData,
                    exercises,
                    quizzes,
                });
            } catch (error) {
                console.error("Error fetching sublesson data:", error);
                setSubLesson(null);
            } finally {
                setLoading(false);
            }
        };

        fetchSubLessonData();
    }, [selectedSubLesson]);


    useEffect(() => {
        const fetchQuizExercises = async () => {
            try {
                const res = await axios.get(`${baseURL}quizRelatedExercises`);
                setQuizExercises(res.data || []);
            } catch (error) {
                console.error("Error fetching quiz related exercises:", error);
            }
        };
        fetchQuizExercises();
    }, []);

    return (
        <>
            <div className="p-4">
                <TitleBar
                    title={course?.title || "Loading..."}
                    subTitle={`${lessons.length} Lessons`}
                    isCourseProgressPage={!showLesson}
                    setShowLesson={setShowLesson}
                    isLessonPage={isLearner ? true : false}
                    isBackTo={true}
                    backPath={dashboardNavigation}
                    backToTitle="Back to Dashboard"
                />
                {
                    (isLogin && isLearner) && (
                        <UserProgress refresh={selectedQuiz ? false : true} />
                    )
                }

                {showLesson ? (
                    <div className="flex max-md:block mt-4 border-t border-[#D0D0D0]">


                        {((selectedContent && selectedContent > 0) || (selectedVideo && selectedVideo > 0)) && subLesson.length > 0 ? (
                            <VideoSideBar
                                courseTitle={course?.title || ""}
                                lessonTitle={
                                    lessons.find((l) => l.id === selectedLessonId)?.title || ""
                                }
                                subLessonTitle={subLesson?.title}
                                selectedContent={selectedContent}
                                setSelectedContent={setSelectedContent}
                                selectedVideo={selectedVideo}
                                setSelectedVideo={setSelectedVideo}
                                subLessonOrderData={subLesson.sublessonOrderByData || []}
                                onCourseClick={() => setShowLesson(false)}
                                onLessonClick={() => {
                                    setSelectedVideo(null);
                                    setSelectedContent(null);
                                }}
                            />
                        ) : selectedQuiz && selectedSubLessonForQuiz ? (
                            <QuizExerciseSideBar
                                courseTitle={course?.title || ""}
                                lessonTitle={lessons.find((l) => l.id === selectedLessonId)?.title || ""}
                                subLessonTitle={selectedSubLessonForQuiz.title}
                                items={selectedQuiz.isExercise ? selectedSubLessonForQuiz.exercises || [] : selectedSubLessonForQuiz.quizzes || []}
                                selectedItem={selectedQuiz}
                                onSelectItem={(item: any) =>
                                    setSelectedQuiz({
                                        ...item,
                                        subLessonId: selectedSubLessonForQuiz.id,
                                        isExercise: !!selectedSubLessonForQuiz.exercises.includes(item),
                                        exerciseId: item.id,
                                    })
                                }
                                onCourseClick={() => setShowLesson(false)}
                                onLessonClick={() => {
                                    setSelectedQuiz(null);
                                    setSelectedSubLessonForQuiz(null);
                                }}
                                onSubLessonClick={() => {
                                    setSelectedQuiz(null);
                                    setSelectedSubLessonForQuiz(null);
                                }}
                                mode={selectedQuiz.isExercise ? "exercise" : "quiz"}
                            />
                        ) : (
                            <LessonsSideBar
                                lessonList={lessons}
                                selectedLessonId={selectedLessonId}
                                selectedSubLesson={selectedSubLesson}
                                isLogin={isLogin}
                                isLearner={isLearner}
                                onSelectLesson={(lessonId: number) => {

                                    setSelectedLessonId(lessonId);
                                    setSelectedSubLesson(null);
                                    setSubLesson({});
                                    setSelectedVideo(null);
                                    setSelectedContent(null);

                                }}
                                onSelectSubLesson={(subLessonId: number, lessonId: number) => {
                                    if (subLessonId !== selectedSubLesson || lessonId !== selectedLessonId) {
                                        setSelectedSubLesson(subLessonId);
                                        setSelectedLessonId(lessonId);
                                        setSubLesson([]);
                                        setSelectedVideo(null);
                                        setSelectedContent(null);
                                    }
                                }}
                            />
                        )}

                        <div className="w-full bg-slate-100 p-4">
                            {loading && (
                                <div className="flex items-center justify-center h-64">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-gray-600 font-medium">Loading...</p>
                                    </div>
                                </div>
                            )}

                            {!loading && selectedQuiz && selectedQuiz.id && selectedQuiz.subLessonId && !showAttemptsPopup ? (
                                <Quiz
                                    key={`${selectedQuiz.isExercise ? "exercise" : "quiz"}-${selectedQuiz.id}-${selectedQuiz.subLessonId}`}
                                    userId={userId}
                                    quizId={selectedQuiz.id}
                                    courseId={courseId}
                                    subLessonId={selectedQuiz.subLessonId}
                                    isExercise={selectedQuiz.isExercise || false}
                                    exerciseId={selectedQuiz.exerciseId}
                                    onFinish={() => {
                                        setSelectedQuiz(null);
                                        setSelectedSubLessonForQuiz(null);
                                    }}
                                />
                            ) : !loading && selectedVideo ? (
                                <VideoPlayer
                                    videoId={selectedVideo}
                                    onClose={() => setSelectedVideo(null)}
                                />
                            ) : !loading && selectedContent ? (
                                <ShowCompleteContent contentId={selectedContent} onClose={() => setSelectedContent(null)} />
                            ) : (
                                !loading && (
                                    <div className="mb-6 space-y-3">
                                        {
                                            (isLogin && isLearner) && (
                                                <LessonProgress lessonId={Number(selectedLessonId)} />
                                            )
                                        }

                                        {
                                            (selectedLessonId && !selectedSubLesson && isLogin && isLearner) && (
                                                <SubLessonsProgress lessonId={Number(selectedLessonId)} setSelectedSubLesson={setSelectedSubLesson} />
                                            )
                                        }

                                        {
                                            (selectedLessonId && selectedSubLesson && subLesson) && (

                                                <div key={subLesson.id} className="bg-white p-4 rounded-md mb-2 shadow-sm">
                                                    <div className="flex gap-6 max-md:block">
                                                        <div className="w-2/3">
                                                            <h3 className="text-md font-semibold text-gray-800">
                                                                {subLesson.title}
                                                            </h3>
                                                            <p className="text-sm text-gray-600 mb-2">
                                                                {subLesson.description}
                                                            </p>

                                                            {subLesson.sublessonOrderByData?.length > 0 && (
                                                                <div className="mb-3">
                                                                    <h4 className="text-sm font-semibold mb-1">Learn</h4>
                                                                    <div className="w-full space-y-2">
                                                                        {subLesson.sublessonOrderByData.map((item: any) => (

                                                                            <>
                                                                                {

                                                                                    (item?.contentId > 0) && (
                                                                                        <div className="cursor-pointer w-full flex items-center gap-2.5" onClick={() => {
                                                                                            setSelectedContent(item?.contentId);
                                                                                            setSelectedSubLesson(item?.subLessonId);

                                                                                        }}>
                                                                                            <span className="material-symbols-outlined text-2xl text-gray-800">
                                                                                                docs
                                                                                            </span>

                                                                                            <span className="text-sm text-blue-600 hover:underline">{item?.contentTitle}</span>
                                                                                        </div>

                                                                                    )
                                                                                }
                                                                                {
                                                                                    (item?.videoId > 0) && (
                                                                                        <div className="cursor-pointer w-full flex items-center gap-2.5" onClick={() => {
                                                                                            setSelectedVideo(item?.videoId)
                                                                                            setSelectedSubLesson(item?.subLessonId)
                                                                                        }}>
                                                                                            <span className="material-symbols-outlined text-2xl text-gray-800">
                                                                                                videocam
                                                                                            </span>
                                                                                            <span className="text-sm text-blue-600 hover:underline">{item?.videoTitle}</span>
                                                                                        </div>

                                                                                    )
                                                                                }
                                                                            </>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="w-1/3 max-md:w-full bg-gray-50 p-3 rounded-md shadow-inner h-fit">
                                                            <h4 className="text-sm font-semibold mb-2">Exercises</h4>
                                                            {subLesson.exercises && subLesson.exercises.length > 0 ? (
                                                                subLesson.exercises.some((ex: any) =>
                                                                    quizExercises.some((qe) => qe.exerciseId === ex.id)
                                                                ) ? (
                                                                    subLesson.exercises.map((ex: any) => {
                                                                        const relatedQuiz = quizExercises.find(
                                                                            (qe) => qe.exerciseId === ex.id
                                                                        );
                                                                        if (!relatedQuiz) return null;

                                                                        return (
                                                                            <div
                                                                                key={ex.id}
                                                                                className="p-2 border-b last:border-none hover:bg-gray-100 rounded-md"
                                                                            >
                                                                                <span className="font-medium">{ex.title}</span>
                                                                                <div className="mt-2">
                                                                                    <button
                                                                                        className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary/80"
                                                                                        onClick={() => {
                                                                                            setSelectedSubLessonForQuiz(subLesson);
                                                                                            setSelectedQuiz({
                                                                                                id: relatedQuiz.exerciseId,
                                                                                                subLessonId: relatedQuiz.subLessonId,
                                                                                                isExercise: true,
                                                                                                exerciseId: relatedQuiz.exerciseId,
                                                                                            });
                                                                                        }}
                                                                                    >
                                                                                        Start
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <p className="text-gray-500 text-sm">No exercises available</p>
                                                                )
                                                            ) : (
                                                                <p className="text-gray-500 text-sm">No exercises available</p>
                                                            )}
                                                        </div>

                                                    </div>

                                                    {
                                                        subLesson.quizzes && subLesson.quizzes.length > 0 && (
                                                            <div className="mr-2 mt-2 border-t border-gray-300">
                                                                {subLesson.quizzes.map((quiz: any) => (
                                                                    <div
                                                                        key={quiz.id}
                                                                        className="p-2 rounded-md mb-2 hover:bg-gray-50"
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <FaQuestionCircle className="text-primary" />
                                                                            <div>
                                                                                <p className="font-medium">{quiz.title}</p>
                                                                                <p className="text-xs text-gray-500">
                                                                                    {quiz.description}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mt-2">
                                                                            <button
                                                                                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                                                                onClick={() => {
                                                                                    if (isLogin && isLearner) {
                                                                                        setSelectedSubLessonForQuiz(subLesson);
                                                                                        setSelectedQuiz({ ...quiz, subLessonId: subLesson.id });
                                                                                    }
                                                                                    else {
                                                                                        setConfirmPopup(true);
                                                                                        setConfirmData({
                                                                                            title: "Please login as learner role.",
                                                                                            confirmBtn: "Ok",
                                                                                            isCancelBtn: false,
                                                                                            isConfirm: async (callback: any) => {
                                                                                                callback();
                                                                                            }
                                                                                        })
                                                                                    }
                                                                                }}
                                                                            >
                                                                                Start Quiz
                                                                            </button>

                                                                            {
                                                                                (isLogin && isLearner) && (
                                                                                    <button
                                                                                        className="mt-2 px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                                                                                        onClick={() => {
                                                                                            setSelectedQuiz(quiz);
                                                                                            setPage(0);
                                                                                            setShowAttemptsPopup(true);
                                                                                        }}
                                                                                    >
                                                                                        <FaEye /> View Attempts
                                                                                    </button>
                                                                                )
                                                                            }


                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )
                                                    }

                                                </div>


                                            )
                                        }


                                    </div>
                                )
                            )}
                        </div>
                    </div>
                ) : (

                    (isLogin && isLearner) && (
                        <CourseProgress courseId={courseId} />
                    )


                )}

                {showAttemptsPopup && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[9999] py-10">
                        <div className="bg-white rounded-lg p-6 w-11/12 max-w-3xl shadow-lg overflow-y-auto max-h-[80vh] mt-10">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold">📜 Quiz Attempt History</h2>
                                <button
                                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                    onClick={() => {
                                        setShowAttemptsPopup(false);
                                        setSelectedSubmission(null);
                                        setAttemptQuestions([]);
                                        setAttempts([]);
                                        setSelectedQuiz(null);
                                        setPage(0);
                                        setSize(5);
                                    }}
                                >
                                    ✖
                                </button>
                            </div>

                            {attemptLoading && !selectedSubmission && (
                                <div className="min-h-[450px] w-full flex items-center justify-center">
                                    <p>Loading attempts...</p>
                                </div>
                            )}

                            {!attemptLoading && !selectedSubmission && attempts.length > 0 && (
                                <div className="min-h-[450px] space-y-3">
                                    {attempts.map((a, i) => (
                                        <div
                                            key={a.id}
                                            className="flex justify-between items-center p-3 border rounded-md hover:bg-gray-50"
                                        >
                                            <div>
                                                <p className="font-medium text-gray-800">
                                                    Attempt {totalCount - ((page * size) + i)}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Submitted At: {new Date(a.submittedAt).toLocaleString()}
                                                </p>
                                                <p className="text-xs text-gray-500">Score: {a.score}</p>
                                            </div>
                                            <button
                                                onClick={() => viewAttemptDetails(a)}
                                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                                            >
                                                <FaEye /> View Attempt
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!selectedSubmission && totalCount > 0 && (
                                <div className="relative mt-4">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                disabled={page === 0}
                                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50 text-sm"
                                            >
                                                Prev
                                            </button>

                                            <span className="text-sm text-center">
                                                Page {page + 1} / {Math.ceil(totalCount / size)}
                                            </span>

                                            <button
                                                disabled={(page + 1) * size >= totalCount}
                                                onClick={() => setPage((p) => p + 1)}
                                                className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50 text-sm"
                                            >
                                                Next
                                            </button>
                                        </div>

                                        {/* Page size selector */}
                                        <div className="sm:absolute sm:right-0 sm:bottom-0 w-full sm:w-auto">
                                            <select
                                                value={size}
                                                onChange={(e) => {
                                                    setSize(Number(e.target.value));
                                                    setPage(0);
                                                }}
                                                className="border px-3 py-2 rounded text-sm w-full sm:w-auto"
                                            >
                                                <option value={5}>5 per page</option>
                                                <option value={10}>10 per page</option>
                                                <option value={20}>20 per page</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                            )}

                            {!attemptLoading && !selectedSubmission && attempts.length === 0 && (
                                <p className="text-gray-500">No attempts found.</p>
                            )}

                            {selectedSubmission && (
                                <div className="mt-6">
                                    <button
                                        onClick={() => {
                                            setSelectedSubmission(null);
                                            setAttemptQuestions([]);
                                        }}
                                        className="mb-4 px-3 py-1 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                                    >
                                        ← Back to Attempts
                                    </button>

                                    <h3 className="text-lg font-semibold mb-2">
                                        Score: {selectedSubmission.score}
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Submitted at:{" "}
                                        {new Date(selectedSubmission.submittedAt).toLocaleString()}
                                    </p>

                                    {attemptLoading ? (
                                        <p>Loading answers...</p>
                                    ) : attemptQuestions.length === 0 ? (
                                        <p className="text-gray-500">No questions found for this submission.</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {attemptQuestions.map((item: any, index: number) => (
                                                <div
                                                    key={item.id ?? index}
                                                    className="p-4 border rounded-lg bg-gray-50"
                                                >
                                                    <h4 className="font-semibold mb-2">
                                                        {index + 1}. {item.question}
                                                    </h4>

                                                    <ul className="space-y-2">
                                                        {item.answers.map((ans: any) => {
                                                            const isSelected = item.selectedAnswerIds?.includes(ans.id);
                                                            const isCorrect = ans.isCorrect;
                                                            const isWrongSelected =
                                                                isSelected && !isCorrect && item.isSelectedAnswerCorrect === false;

                                                            return (
                                                                <li
                                                                    key={ans.id}
                                                                    className={`p-2 border rounded flex justify-between items-center ${isSelected
                                                                        ? isCorrect
                                                                            ? "bg-green-100 border-green-400"
                                                                            : "bg-red-100 border-red-400"
                                                                        : "bg-gray-50"
                                                                        }`}
                                                                >
                                                                    <span>{ans.answerText}</span>

                                                                    <div className="flex items-center gap-2">
                                                                        {/* Selected & correct */}
                                                                        {isSelected && isCorrect && (
                                                                            <FaCheckCircle className="text-green-600" />
                                                                        )}

                                                                        {/* Selected & wrong */}
                                                                        {isWrongSelected && (
                                                                            <FaTimesCircle className="text-red-600" />
                                                                        )}

                                                                        {/* Not selected but correct */}
                                                                        {!isSelected && isCorrect && (
                                                                            <FaCheckCircle className="text-green-400" />
                                                                        )}
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>


                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <ConfirmPopup />
        </>

    );
};